import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ExternallyVerifiedHealthInformationProto } from "@noom/noom-contracts/noom_contracts/usermodel/baselined/health/external/externally_verified_health_information";
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetContent,
  BottomSheetHandle,
  BottomSheetHeader,
  BottomSheetOverlay,
  Box,
  Checkbox,
  Heading,
  Image,
  Loader,
  NeoColor,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import { NeoButton } from "@/components";
import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { useGoto, useMedicalHistory, useTrackEvent } from "@/hooks";
import {
  type EntitlementType,
  buildMedicalHistorySavePayload,
  mapHealthInformationToEntitlements,
} from "@/utils/externalHealthRecords";

import { BaseInterstitial, BaseOutline } from "../core";

const QUESTION_ID = "connectHealthRecordsPersonalization";

type EntitlementCardProps = {
  description: string;
  entitlementType: EntitlementType;
  isChecked: boolean;
  onChange: (entitlementType: EntitlementType, checked: boolean) => void;
  title: string;
};

const EntitlementCard: React.FC<EntitlementCardProps> = ({
  entitlementType,
  title,
  description,
  isChecked,
  onChange,
}) => (
  <Box
    alignItems="flex-start"
    as="label"
    backgroundColor="white"
    borderRadius="16px"
    cursor="pointer"
    display="flex"
    gap={Spacing[4]}
    padding={Spacing[4]}
    width="100%"
  >
    <Checkbox
      isChecked={isChecked}
      onChange={({ target: { checked } }) => onChange(entitlementType, checked)}
      size="lg"
    />
    <Stack spacing={Spacing[1]}>
      <Heading fontSize="20px" fontWeight={400} lineHeight="26px">
        {title}
      </Heading>
      <Text
        as="p"
        color={NeoColor.greyscale.gray4}
        fontSize="12px"
        lineHeight="18px"
      >
        {description}
      </Text>
    </Stack>
  </Box>
);

const ConnectHealthRecordsPersonalization: React.FC<
  QuestionComponentProps
> = () => {
  const { openApp } = useGoto();
  const { getMedicalHistory, saveMedicalHistory } = useMedicalHistory();
  const [healthInformation, setHealthInformation] =
    useState<ExternallyVerifiedHealthInformationProto>();
  const [checkedEntitlements, setCheckedEntitlements] = useState<
    EntitlementType[]
  >([]);
  const [supportedEntitlements, setSupportedEntitlements] = useState<
    EntitlementType[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { trackAnonEvent, trackIdentifiedPageViewed } = useTrackEvent();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchMedicalHistory = async () => {
      try {
        const result = await getMedicalHistory();
        const entitlements = result
          ? mapHealthInformationToEntitlements(result)
          : [];
        if (result) {
          setHealthInformation(result);
          setSupportedEntitlements(entitlements);
          setCheckedEntitlements(entitlements);
        }
        trackAnonEvent(
          "b2bEnrollmentAnonHealthRecordsPersonalizationPageViewed",
          {
            supportedEntitlements: entitlements,
          },
        );
        trackIdentifiedPageViewed(QUESTION_ID);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMedicalHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSupportedEntitlements = supportedEntitlements.length > 0;

  const heading = hasSupportedEntitlements
    ? t(`${QUESTION_ID}.heading`)
    : t(`${QUESTION_ID}.noRecords.heading`);
  const subheading = hasSupportedEntitlements
    ? t(`${QUESTION_ID}.subheading`)
    : t(`${QUESTION_ID}.noRecords.subheading`);
  const ctaText = hasSupportedEntitlements
    ? t(`${QUESTION_ID}.ctaText`)
    : t(`${QUESTION_ID}.noRecords.ctaText`);

  const handleCheckboxChange = (
    entitlementType: EntitlementType,
    checked: boolean,
  ) => {
    setCheckedEntitlements((prev) =>
      checked
        ? [...prev, entitlementType]
        : prev.filter((e) => e !== entitlementType),
    );
  };

  const handleSave = async () => {
    const payload = buildMedicalHistorySavePayload(
      healthInformation,
      checkedEntitlements,
    );

    if (!payload) {
      return;
    }

    trackAnonEvent("b2bEnrollmentAnonQuestionAnswered", {
      pageId: QUESTION_ID,
      questionId: QUESTION_ID,
      answerIds: checkedEntitlements,
      answerTexts: checkedEntitlements.map((entitlement) =>
        t(`${QUESTION_ID}.entitlements.${entitlement}.title`),
      ),
    });

    await saveMedicalHistory(payload);
    openApp();
  };

  const handleCtaClick = async () => {
    if (hasSupportedEntitlements && checkedEntitlements.length === 0) {
      setIsModalOpen(true);
      return;
    }

    await handleSave();
  };

  const handleProceedWithoutUpdates = async () => {
    setIsModalOpen(false);
    await handleSave();
  };

  if (isLoading) {
    return (
      <BaseOutline>
        <Box m="auto">
          <Loader colorScheme="primary" />
        </Box>
      </BaseOutline>
    );
  }

  return (
    <BaseInterstitial
      backgroundColor={NeoColor.blue.skyLight}
      onClickBack={() => null}
      onClickNext={() => Promise.resolve()}
      questionId={QUESTION_ID}
    >
      <Stack
        maxWidth="400px"
        padding="var(--spacing-xxxh) 0"
        spacing={Spacing[6]}
      >
        <Stack spacing={Spacing[4]}>
          <Image alt="" src="/assets/img/personalization.svg" width="48px" />
          <Heading fontSize="32px" fontWeight={400} lineHeight="36px">
            {heading}
          </Heading>
          <Text
            color={NeoColor.greyscale.gray4}
            fontSize="16px"
            lineHeight="24px"
          >
            {subheading}
          </Text>
        </Stack>

        {hasSupportedEntitlements && (
          <Stack spacing={Spacing[4]}>
            {supportedEntitlements.map((entitlementType) => (
              <EntitlementCard
                description={t(
                  `${QUESTION_ID}.entitlements.${entitlementType}.description`,
                )}
                entitlementType={entitlementType}
                key={entitlementType}
                isChecked={checkedEntitlements.includes(entitlementType)}
                onChange={handleCheckboxChange}
                title={t(
                  `${QUESTION_ID}.entitlements.${entitlementType}.title`,
                )}
              />
            ))}
          </Stack>
        )}
      </Stack>
      {hasSupportedEntitlements ? (
        <Box
          backgroundColor={NeoColor.blue.skyLight}
          bottom="0"
          left="50%"
          padding="var(--spacing-m)"
          position="fixed"
          transform="translateX(-50%)"
          width="100%"
        >
          <NeoButton onClick={handleCtaClick}>{ctaText}</NeoButton>
        </Box>
      ) : (
        <NeoButton onClick={handleCtaClick}>{ctaText}</NeoButton>
      )}

      <BottomSheet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <BottomSheetOverlay />
        <BottomSheetContent>
          <BottomSheetHandle />
          <BottomSheetHeader>
            <Heading fontSize="24px" fontWeight={400} lineHeight="32px">
              {t(`${QUESTION_ID}.deselectionModal.title`)}
            </Heading>
          </BottomSheetHeader>
          <BottomSheetBody>
            <Stack spacing={Spacing[3]}>
              <NeoButton onClick={handleProceedWithoutUpdates}>
                {t(`${QUESTION_ID}.deselectionModal.confirmText`)}
              </NeoButton>
              <NeoButton
                colorScheme="secondary"
                onClick={() => setIsModalOpen(false)}
              >
                {t(`${QUESTION_ID}.deselectionModal.cancelText`)}
              </NeoButton>
            </Stack>
          </BottomSheetBody>
        </BottomSheetContent>
      </BottomSheet>
    </BaseInterstitial>
  );
};

export const Q_CONNECT_HEALTH_RECORDS_PERSONALIZATION: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: () => true,
  component: ConnectHealthRecordsPersonalization,
};
